import { Content, ContentValueLocalizable, Localized, UnfetchedFieldValue } from "@robe/core-types";

export type UnresolvedUnfetchedFieldValue = UnfetchedFieldValue | Array<UnfetchedFieldValue> | Array<Record<string, UnfetchedFieldValue>>;
export type LocalizedUnresolvedUnfetchedFieldValue = Localized<UnresolvedUnfetchedFieldValue>;

/**
 * @property {string} field - Content values field or list object field.
 * @property {string} ofListObjectField - If `field` is in list object this stores the name of field that owns the list object.
 */
export interface ContentUnfetchedValuesFieldsObject {
  field: string;
  ofListObjectField?: string;
}

export interface ContentUnfetchedValuesFieldsObjectResolved extends ContentUnfetchedValuesFieldsObject {
  id: number;
}

interface ResolveContentIdsFieldsProps {
  content: Content;
  fields: ContentUnfetchedValuesFieldsObject[];
}

/**
 * Get IDs from unfetched content values fields.
 * IDs check may not be unique.
 * It is important to check for unique IDs from the result object
 * so that even if there are same IDs they can easily be mapped to original field.
 * */
export const resolveContentUnfetchedValuesFieldsIds = ({
  content,
  fields,
}: ResolveContentIdsFieldsProps): ContentUnfetchedValuesFieldsObjectResolved[] => {
  if (fields.length === 0) {
    return [];
  }

  const contentUnfetchedValuesFieldsResolved: ContentUnfetchedValuesFieldsObjectResolved[] = [];

  // Collect content IDs
  const resolveAndPushValue = (value: UnfetchedFieldValue | Array<UnfetchedFieldValue>, field: string, ofListObjectField?: string) =>
    Array.isArray(value)
      ? value.forEach(
        (v) =>
          v?.id &&
            contentUnfetchedValuesFieldsResolved.push({
              id: v.id,
              field,
              ...(ofListObjectField && { ofListObjectField }),
            })
      )
      : value?.id &&
        contentUnfetchedValuesFieldsResolved.push({
          id: value.id,
          field,
          ...(ofListObjectField && { ofListObjectField }),
        });

  // Loop through values fields names
  fields.forEach(({ field, ofListObjectField }) => {
    const values = content.values as Record<string, ContentValueLocalizable>;

    const fieldName = ofListObjectField ?? field;

    if (values?.[fieldName]) {
      const value = values[fieldName] as UnresolvedUnfetchedFieldValue | LocalizedUnresolvedUnfetchedFieldValue;

      // If `id` field is present value or value is array value is considered non localized
      if (Array.isArray(value) || value?.id) {
        // List object of unfetched fields
        if (ofListObjectField) {
          const listObjectValue = value as Array<Record<string, UnfetchedFieldValue>>;

          listObjectValue.forEach((v) => {
            const listObjectFieldValue = v[field];

            if (listObjectFieldValue && typeof listObjectFieldValue === "object") {
              const valEntries = Object.entries(listObjectFieldValue);

              // Localized field in non localized list object
              if (valEntries.every(([key]) => key !== "id")) {
                valEntries.forEach(([, localizedValue]) => {
                  resolveAndPushValue(
                    localizedValue as unknown as UnfetchedFieldValue | Array<UnfetchedFieldValue>,
                    field,
                    ofListObjectField
                  );
                });

                return;
              }
            }

            // Non localized field in non localized list object
            resolveAndPushValue(listObjectFieldValue, field, ofListObjectField);
          });

          return;
        }

        const nonLocalizedValue = value as UnfetchedFieldValue | Array<UnfetchedFieldValue>;

        resolveAndPushValue(nonLocalizedValue, field);
        return;
      }

      const localizedValue = value as LocalizedUnresolvedUnfetchedFieldValue;

      Object.keys(value).forEach((locale) => {
        // Localized list object unfetched fields
        if (ofListObjectField) {
          const listObjectValue = localizedValue[locale] as Array<Record<string, UnfetchedFieldValue>>;

          listObjectValue.forEach((v) => resolveAndPushValue(v[field], field, ofListObjectField));
        }

        resolveAndPushValue(localizedValue[locale] as UnfetchedFieldValue | Array<UnfetchedFieldValue>, field);
      });
    }
  });

  return contentUnfetchedValuesFieldsResolved;
};
